import axios from 'src/utils/axios';
import helpers from 'src/helpers';
import { NOTIFICATION_STATUS } from 'src/constants';
import { addNotification } from 'src/actions/notificationActions';

// CON-1532 Phase 9 — Page Column Defaults admin actions.
//
// Talks to the new /api/page-preferences/admin/* endpoints (scopes / GET /
// PUT / DELETE) added in the same phase. All endpoints are AND-gated on
// ADMIN_TOOLS_VIEW + PAGE_COLUMN_DEFAULTS_ADMIN at the router layer; the
// action creators just forward.
//
// State shape this reducer owns (see reducers/pageColumnDefaultsAdminReducer.js):
//   {
//     scopes: [{ key, label, companyId }],
//     currentCompanyId: number | null,
//     currentPageKey: 'CONTACTS' | 'EVENT_GUESTS' | 'GROUP_CONTACTS',
//     prefsByScope: { [`${companyId}-${pageKey}`]: { version, columns, sortBy } | null },
//     loading: boolean,
//     error: string | null,
//   }

// ── Action types ──────────────────────────────────────────────────────

export const PCD_GET_SCOPES = '@pcd-admin/get-scopes';
export const PCD_GET_SCOPES_SUCCESS = '@pcd-admin/get-scopes-success';
export const PCD_GET_SCOPES_ERROR = '@pcd-admin/get-scopes-error';

export const PCD_GET_PREFS = '@pcd-admin/get-prefs';
export const PCD_GET_PREFS_SUCCESS = '@pcd-admin/get-prefs-success';
export const PCD_GET_PREFS_ERROR = '@pcd-admin/get-prefs-error';

export const PCD_SAVE_PREFS = '@pcd-admin/save-prefs';
export const PCD_SAVE_PREFS_SUCCESS = '@pcd-admin/save-prefs-success';
export const PCD_SAVE_PREFS_ERROR = '@pcd-admin/save-prefs-error';

export const PCD_DELETE_PREFS = '@pcd-admin/delete-prefs';
export const PCD_DELETE_PREFS_SUCCESS = '@pcd-admin/delete-prefs-success';
export const PCD_DELETE_PREFS_ERROR = '@pcd-admin/delete-prefs-error';

export const PCD_SET_CURRENT_SCOPE = '@pcd-admin/set-current-scope';
export const PCD_SET_CURRENT_PAGE_KEY = '@pcd-admin/set-current-page-key';
export const PCD_CLEAR = '@pcd-admin/clear';

// ── Helpers ───────────────────────────────────────────────────────────

// Cache key for the prefsByScope map. Same shape used in the reducer.
export const prefsKey = (companyId, pageKey) => `${companyId}-${pageKey}`;

// ── Action creators ───────────────────────────────────────────────────

/** *************************************************************************
 * setCurrentScope
 *
 * Records the currently-selected admin scope (companyId). The view derives
 * companyKey from this for the dropdown selected value.
 *************************************************************************** */
export const setCurrentScope = (companyId) => ({
  type: PCD_SET_CURRENT_SCOPE,
  payload: { companyId: (companyId === null || companyId === undefined) ? null : Number(companyId) },
});

/** *************************************************************************
 * setCurrentPageKey
 *
 * Records the currently-selected tab. One of CONTACTS / EVENT_GUESTS /
 * GROUP_CONTACTS.
 *************************************************************************** */
export const setCurrentPageKey = (pageKey) => ({
  type: PCD_SET_CURRENT_PAGE_KEY,
  payload: { pageKey: pageKey || null },
});

/** *************************************************************************
 * clearPageColumnDefaultsAdmin
 *
 * Resets the slice on view unmount.
 *************************************************************************** */
export const clearPageColumnDefaultsAdmin = () => ({ type: PCD_CLEAR });

/** *************************************************************************
 * getPageColumnDefaultsScopes
 *
 * GET /api/page-preferences/admin/scopes
 * Returns Generic + all companies (alphabetical).
 *************************************************************************** */
export const getPageColumnDefaultsScopes = () => async (dispatch) => {
  dispatch({ type: PCD_GET_SCOPES });
  try {
    const response = await axios.get('/api/page-preferences/admin/scopes');
    dispatch({
      type: PCD_GET_SCOPES_SUCCESS,
      payload: { scopes: response?.data?.scopes || [] },
    });
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      PCD_GET_SCOPES_ERROR,
      helpers.transform.toString(message || error || 'Could not load scopes'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: PCD_GET_SCOPES_ERROR });
  }
};

/** *************************************************************************
 * getPageColumnDefaultsPrefs
 *
 * GET /api/page-preferences/admin?companyId=X&pageKey=Y
 * Loads the saved preferences blob for one (scope, pageKey).
 *************************************************************************** */
export const getPageColumnDefaultsPrefs = (companyId, pageKey) => async (dispatch) => {
  dispatch({ type: PCD_GET_PREFS, payload: { companyId, pageKey } });
  try {
    const response = await axios.get(
      `/api/page-preferences/admin?companyId=${companyId}&pageKey=${pageKey}`,
    );
    dispatch({
      type: PCD_GET_PREFS_SUCCESS,
      payload: {
        companyId,
        pageKey,
        preferences: response?.data?.preferences || null,
      },
    });
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      PCD_GET_PREFS_ERROR,
      helpers.transform.toString(message || error || 'Could not load preferences'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: PCD_GET_PREFS_ERROR, payload: { companyId, pageKey } });
  }
};

/** *************************************************************************
 * savePageColumnDefaultsPrefs
 *
 * PUT /api/page-preferences/admin
 * Upserts (companyId, pageKey, columns). On success refetches via
 * getPageColumnDefaultsPrefs so the preview reflects what's persisted.
 *************************************************************************** */
export const savePageColumnDefaultsPrefs = (
  companyId,
  pageKey,
  columns,
) => async (dispatch) => {
  dispatch({ type: PCD_SAVE_PREFS, payload: { companyId, pageKey } });
  try {
    await axios.put('/api/page-preferences/admin', { companyId, pageKey, columns });
    dispatch({ type: PCD_SAVE_PREFS_SUCCESS, payload: { companyId, pageKey } });
    dispatch(addNotification(
      PCD_SAVE_PREFS_SUCCESS,
      'Page column defaults saved.',
      NOTIFICATION_STATUS.SUCCESS,
    ));
    // Refresh the cached prefs for this scope+pageKey.
    await dispatch(getPageColumnDefaultsPrefs(companyId, pageKey));
    return { success: true };
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      PCD_SAVE_PREFS_ERROR,
      helpers.transform.toString(message || error || 'Save failed'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: PCD_SAVE_PREFS_ERROR, payload: { companyId, pageKey } });
    return { success: false };
  }
};

/** *************************************************************************
 * deletePageColumnDefaultsPrefs
 *
 * DELETE /api/page-preferences/admin?companyId=X&pageKey=Y
 * Removes the row so the resolver falls through to the generic default
 * on the next read. Rejected by the backend with 400 if companyId=0.
 *************************************************************************** */
export const deletePageColumnDefaultsPrefs = (companyId, pageKey) => async (dispatch) => {
  dispatch({ type: PCD_DELETE_PREFS, payload: { companyId, pageKey } });
  try {
    await axios.delete(
      `/api/page-preferences/admin?companyId=${companyId}&pageKey=${pageKey}`,
    );
    dispatch({ type: PCD_DELETE_PREFS_SUCCESS, payload: { companyId, pageKey } });
    dispatch(addNotification(
      PCD_DELETE_PREFS_SUCCESS,
      'Page column default removed.',
      NOTIFICATION_STATUS.SUCCESS,
    ));
    await dispatch(getPageColumnDefaultsPrefs(companyId, pageKey));
    return { success: true };
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification(
      PCD_DELETE_PREFS_ERROR,
      helpers.transform.toString(message || error || 'Reset failed'),
      NOTIFICATION_STATUS.ERROR,
    ));
    dispatch({ type: PCD_DELETE_PREFS_ERROR, payload: { companyId, pageKey } });
    return { success: false };
  }
};
